/**
 * FormazioneCountdown — card sulla home con countdown alla deadline schiera
 * formazione (= orario prima partita giornata SA - 15 min).
 *
 * Sorgente orari: football-data.org (via /admin/football-data/giornata/{N}).
 *
 * Andrea 2026-05-23: 4 stati distinti in base alla combinazione
 * (consegnata, deadline scaduta):
 *
 *   1. !consegnata + deadline non scaduta  → "Formazione da consegnare"
 *                                              + countdown, click → /formazione
 *   2.  consegnata + deadline non scaduta  → "Formazione consegnata · Modifica"
 *                                              + countdown verde, click → /formazione
 *   3.  consegnata + deadline scaduta      → "Formazione consegnata" (definitiva)
 *   4. !consegnata + deadline scaduta      → "Formazione non consegnata"
 *
 * Stato URGENTE (countdown arancione) si applica in 1 e 2 quando rimane <1h.
 *
 * Andrea 2026-09-10: in alto a destra, oltre alla giornata di Serie A,
 * c'e' anche la giornata della NOSTRA competizione (quella del
 * calendario fanta). Su desktop il countdown sta al centro e il bottone
 * "inserisci/modifica formazione" sotto la scritta di stato, non di
 * fianco.
 */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useGiornataInfo } from "../../hooks/useFootballData";
import { localeCorrente } from "../../i18n";
import { tr } from "../../i18n";

const GRACE_MINUTES = 15;

interface Props {
  matchday: number | null;
  /** Giornata del calendario fanta (quella della nostra competizione). */
  giornata?: number | null;
  /** Indica se la formazione è già stata consegnata (mostra "consegnata" invece di countdown) */
  consegnata?: boolean;
  /** Competizione a cui si riferisce (Andrea 2026-09-09): con campionato
   * e coppa nella stessa giornata la formazione si consegna per ognuna,
   * quindi il countdown dice PER QUALE e ci porta dritti su quella. */
  competizioneId?: string | null;
  competizioneNome?: string | null;
  /** Andrea 2026-09-17: c'e' ancora una giornata in corso. Schiera resta
   * su quella (bloccata) finche' non viene calcolata, quindi qui niente
   * bottone: solo il countdown, spento, e la spiegazione. */
  inAttesa?: boolean;
}

/** Pastiglia gialla "Inserisci/Modifica formazione". Andrea 2026-09-09:
 * il blocco countdown sembrava solo informativo, ora si vede che si
 * preme. È uno <span> e non un <button> perché sta dentro un <Link>. */
function BottoneFormazione({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-accent text-ink font-bold text-[11px] tracking-wide shadow-sm">
      {label}
    </span>
  );
}

function formatRemaining(msRemaining: number): string {
  if (msRemaining <= 0) return "00:00:00";
  const totalSec = Math.floor(msRemaining / 1000);
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const mins = Math.floor((totalSec % 3600) / 60);
  const secs = totalSec % 60;
  const pad = (n: number): string => String(n).padStart(2, "0");
  if (days > 0)
    return tr("{{v1}}{{v2}} {{v3}}:{{v4}}:{{v5}}", {
      v1: days,
      v2: tr("g"),
      v3: pad(hours),
      v4: pad(mins),
      v5: pad(secs),
    });
  return `${pad(hours)}:${pad(mins)}:${pad(secs)}`;
}

export function FormazioneCountdown({
  matchday,
  giornata = null,
  consegnata = false,
  competizioneId = null,
  competizioneNome = null,
  inAttesa = false,
}: Props) {
  const linkFormazione = competizioneId
    ? `/formazione?competizione=${encodeURIComponent(competizioneId)}`
    : "/formazione";
  const { data, isLoading, isError, error } = useGiornataInfo(matchday);
  const [now, setNow] = useState<number>(() => Date.now());

  // Tick ogni secondo per aggiornare il countdown
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // Stato di errore esplicito (football-data.org non disponibile)
  if (isError) {
    const err = error as { response?: { data?: { detail?: string } }; message?: string };
    const detail = err.response?.data?.detail ?? err.message ?? "errore";
    return (
      <div className="bg-bg-card border border-warning/30 rounded-card p-4">
        <div className="font-mono text-[10px] uppercase tracking-wider text-warning mb-1">
          {tr("Deadline formazione")}
        </div>
        <div className="text-sm text-fg-muted">
          {tr("⚠ Orario prima partita non disponibile da football-data.org.")}
        </div>
        <div className="text-xs text-fg-muted mt-1">{detail}</div>
      </div>
    );
  }

  if (matchday === null || isLoading || !data) {
    return (
      <div className="bg-bg-card border border-primary/20 rounded-card p-4">
        <div className="font-mono text-[10px] uppercase tracking-wider text-fg-muted mb-1">
          {tr("Deadline formazione")}
        </div>
        <div className="text-sm text-fg-muted">
          {matchday === null ? tr("Nessuna giornata da giocare.") : tr("Caricamento orari…")}
        </div>
      </div>
    );
  }

  if (!data.prima_partita_at) {
    return (
      <div className="bg-bg-card border border-primary/20 rounded-card p-4">
        <div className="font-mono text-[10px] uppercase tracking-wider text-fg-muted mb-1">
          Deadline formazione · giornata {data.matchday}
        </div>
        <div className="text-sm text-fg-muted">
          {tr("Nessuna partita Serie A trovata per questa giornata.")}
        </div>
      </div>
    );
  }

  const primaPartitaMs = new Date(data.prima_partita_at).getTime();
  const deadlineMs = primaPartitaMs - GRACE_MINUTES * 60 * 1000;
  const remaining = deadlineMs - now;

  const dataLeggibile = new Date(primaPartitaMs).toLocaleString(localeCorrente(), {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
  const deadlineLeggibile = new Date(deadlineMs).toLocaleString(localeCorrente(), {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });

  const deadlineScaduta = remaining <= 0;
  const isUrgente = !deadlineScaduta && remaining < 60 * 60 * 1000;

  // "Champions League · Giornata 3 · SA 7" — la giornata nostra prima di
  // quella di Serie A, che serve solo a capire quando si gioca.
  const etichettaGiornata = [
    competizioneNome,
    giornata ? tr("Giornata {{n}}", { n: giornata }) : null,
    tr("SA {{v1}}", { v1: data.matchday }),
  ]
    .filter(Boolean)
    .join(" · ");

  // === Stato 0 (Andrea 2026-09-17): giornata precedente ancora in corso.
  // Non e' un link: premendo si finirebbe sulla formazione bloccata della
  // giornata che si sta giocando.
  if (inAttesa && !deadlineScaduta) {
    return (
      <div className="rounded-card p-4 border border-primary/20 bg-bg-card">
        <div className="mb-2 flex items-start justify-between gap-2">
          <span className="font-mono text-[10px] uppercase tracking-wider text-fg-muted">
            {consegnata ? tr("✓ Formazione consegnata") : tr("⏳ Formazione · non ancora aperta")}
          </span>
          <span className="font-mono text-[10px] uppercase tracking-wider text-fg-muted text-right max-w-[45%] shrink-0">
            {etichettaGiornata}
          </span>
        </div>
        <div className="font-display text-3xl tracking-wide tabular-nums text-fg-muted md:text-center">
          {formatRemaining(remaining)}
        </div>
        <div className="text-xs text-fg-muted mt-1 md:text-center">
          Si potrà schierare appena la giornata in corso viene calcolata: fino ad allora Schiera
          resta su quella. Consegna entro {deadlineLeggibile}.
        </div>
      </div>
    );
  }

  // === Stato 3: formazione consegnata + deadline scaduta → "Formazione consegnata" definitiva
  if (consegnata && deadlineScaduta) {
    return (
      <div className="bg-success/10 border border-success/40 rounded-card p-4">
        <div className="font-mono text-[10px] uppercase tracking-wider text-success mb-1">
          ✓ Giornata bloccata · {etichettaGiornata}
        </div>
        <div className="font-display text-xl text-success">{tr("Formazione consegnata")}</div>
        <div className="text-xs text-fg-muted mt-1">
          Prima partita alle {dataLeggibile}. Non e' piu' modificabile.
        </div>
      </div>
    );
  }

  // === Stato 4: nessuna formazione + deadline scaduta → "Formazione non consegnata"
  if (!consegnata && deadlineScaduta) {
    return (
      <div className="bg-danger/10 border border-danger/40 rounded-card p-4">
        <div className="font-mono text-[10px] uppercase tracking-wider text-danger mb-1">
          ⚠ Deadline scaduta · {etichettaGiornata}
        </div>
        <div className="font-display text-xl text-danger">{tr("Formazione non consegnata")}</div>
        <div className="text-xs text-fg-muted mt-1">
          Verra' applicato il punteggio fisso dal regolamento. Prima partita alle {dataLeggibile}.
        </div>
      </div>
    );
  }

  // === Stato 2: formazione consegnata + deadline non scaduta → "Formazione consegnata · Modifica"
  if (consegnata) {
    return (
      <Link
        to={linkFormazione}
        className="block rounded-card p-4 border border-success/40 bg-success/10 hover:bg-success/15 transition hover:scale-[1.01]"
        title={tr("Modifica formazione (entro la deadline)")}
      >
        <div className="mb-2 flex items-start justify-between gap-2">
          <div className="min-w-0">
            <span className="font-mono text-[10px] uppercase tracking-wider text-success">
              {tr("✓ Formazione consegnata")}
            </span>
            <div className="mt-1.5">
              <BottoneFormazione label={tr("Modifica formazione")} />
            </div>
          </div>
          <span className="font-mono text-[10px] uppercase tracking-wider text-fg-muted text-right max-w-[45%] shrink-0">
            {etichettaGiornata}
          </span>
        </div>
        <div className="font-display text-3xl tracking-wide tabular-nums text-success md:text-center">
          {formatRemaining(remaining)}
        </div>
        <div className="text-xs text-fg-muted mt-1 md:text-center">
          Modificabile entro {deadlineLeggibile} (15 min prima del fischio).
        </div>
      </Link>
    );
  }

  // === Stato 1: nessuna formazione + deadline non scaduta → "Formazione da consegnare"
  return (
    <Link
      to={linkFormazione}
      className={`block rounded-card p-4 border transition hover:scale-[1.01] ${
        isUrgente
          ? "bg-warning/10 border-warning/40 hover:bg-warning/15"
          : "bg-bg-card border-accent/30 hover:bg-bg-card-hover"
      }`}
      title={tr("Schiera la formazione")}
    >
      <div className="mb-2 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <span
            className={`font-mono text-[10px] uppercase tracking-wider ${
              isUrgente ? "text-warning" : "text-fg-muted"
            }`}
          >
            {isUrgente
              ? tr("⏰ Formazione da consegnare · ultime ore")
              : tr("⏱ Formazione da consegnare")}
          </span>
          <div className="mt-1.5">
            <BottoneFormazione label={tr("Inserisci formazione")} />
          </div>
        </div>
        <span className="font-mono text-[10px] uppercase tracking-wider text-fg-muted text-right max-w-[45%] shrink-0">
          {etichettaGiornata}
        </span>
      </div>
      <div
        className={`font-display text-3xl tracking-wide tabular-nums md:text-center ${
          isUrgente ? "text-warning" : "text-accent"
        }`}
      >
        {formatRemaining(remaining)}
      </div>
      <div className="text-xs text-fg-muted mt-1 md:text-center">
        Prima partita: {dataLeggibile} · consegnare entro {deadlineLeggibile}
      </div>
    </Link>
  );
}
